import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="c8rglwcgvfy5">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="c6eo4p931n6u">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="19uyjn1t4h08e" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="14x6jjkibq7t4" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="zzwtq3r0vu4r" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="33g4m9bgiracz" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="2yfqfrxukg8h2">
      <Navbar data-id="element-7" data-mp="3i4xzso2dw2xd" />
      <FoundingFundersPopup data-id="element-8" data-mp="3i4egb2x356h8" />
      <main data-id="element-9" data-mp="3n2pprcjgxeyx">
        <Hero data-id="element-10" data-mp="3n2g39yxmtpxz" />
        <ConsularBanner data-id="element-11" data-mp="3n2zjnb7mw69w" />
        <Mission data-id="element-12" data-mp="3n27mhvye20hi" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-14" data-mp="387jjcoggne52" />} data-id="element-13" data-mp="384xb0mlqwuls">
          <OurPeopleStories data-id="element-15" data-mp="3d49ztcw6gbr0" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-17" data-mp="3d6mosa0crmdk" />} data-id="element-16" data-mp="3d2ywtfak380y">
          <MakeDifferenceCTA data-id="element-18" data-mp="2zdewsh8d0ovc" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-19" data-mp="2zbke79kk98uf">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-20" data-mp="343lmkkelyff6" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-22" data-mp="2pg0lsx57gsaf" />} data-id="element-21" data-mp="2pfh8xuuewu39">
          <NewsBlog data-id="element-23" data-mp="2pgsbjrc4fvss" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-25" data-mp="2uf3owc1mkofr" />} data-id="element-24" data-mp="2uditylbef3hd">
          <MediaScroller data-id="element-26" data-mp="2uebs4xwakknw" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-28" data-mp="2ge10vtr2l427" />} data-id="element-27" data-mp="2gej7nkbcz0od">
          <CommunityStrength data-id="element-29" data-mp="2gesw33gr8zry" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-31" data-mp="2kg4eop17okqn" />} data-id="element-30" data-mp="2kfmkdxy40gld">
          <MissionPanels data-id="element-32" data-mp="2kfcv6ckw2dgq" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-34" data-mp="30vnb80g7vcbv" />} data-id="element-33" data-mp="30v3tokuhapyd">
          <Leadership data-id="element-35" data-mp="30uvf8oipjcl2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-37" data-mp="2vvr966r2xuyy" />} data-id="element-36" data-mp="2vwanldhiu188">
          <AdvisoryCouncil data-id="element-38" data-mp="2vxlsjgmpdf0x" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-40" data-mp="2qwob569iop0h" />} data-id="element-39" data-mp="2qx7hrqtpvqz7">
          <MakeDifferenceCTA data-id="element-41" data-mp="2qvweds46ol8m" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-43" data-mp="2lymjvy1xtjdx" />} data-id="element-42" data-mp="2ly4o12j6t76j">
          <BusinessInvestments data-id="element-44" data-mp="2lxbfpx4ky9ma" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-46" data-mp="2hx1boq0clta2" />} data-id="element-45" data-mp="2hwj4wzg27wnw">
          <ConsularAffairs data-id="element-47" data-mp="2hxb0wbdkjwep" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-49" data-mp="2d59fnzyfavcj" />} data-id="element-48" data-mp="2d5t1vsv3w5wd">
          <SocialMediaVoices data-id="element-50" data-mp="2d514cchy7y3g" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-52" data-mp="2866nhg3jkjlz" />} data-id="element-51" data-mp="286pvo53e3tmt">
          <NonprofitDirectory data-id="element-53" data-mp="287hqvet2rpck" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-55" data-mp="23852gpmk2a7v" />} data-id="element-54" data-mp="237n1xgsv19u5">
          <HondurasQuiz data-id="element-56" data-mp="238xzuzzmjndc" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-58" data-mp="1y91yl8ibaa1h" />} data-id="element-57" data-mp="1y8iqejigr00n">
          <ExploreHonduras data-id="element-59" data-mp="1y78pzno51fqu" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-61" data-mp="1u6f1as2vfwww" />} data-id="element-60" data-mp="1u6x6ie7ihlgy">
          <FAQ data-id="element-62" data-mp="1u889wcx1or7j" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-63" data-mp="1u9b2qqdxh3q3">
        <Footer data-id="element-64" data-mp="9po0cvmlerzm" />
      </Suspense>
      <Suspense fallback={null} data-id="element-65" data-mp="9oczp4og20cq">
        <ChatbaseWidget data-id="element-66" data-mp="dqho9p01c6si" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  // The site opens directly on the English main page; language is switched from
  // the toggle in the navigation bar rather than a separate entry screen.
  const [showLanding, setShowLanding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    // Language switching keeps the visitor on the current page.
    setShowLanding(false);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-68" data-mp="1aduyls4ofo1i" />} data-id="element-67" data-mp="1acaekwfznnhw">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-69" data-mp="1edlw0eopl2f0" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-70" data-mp="1oaqm2plodi13">
        <Suspense fallback={<SectionLoader data-id="element-72" data-mp="1yex9zpujuhwk" />} data-id="element-71" data-mp="1o8xb4y414bna">
          <ConsularPage data-id="element-73" data-mp="1yg8a9fosd7ix" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-74" data-mp="1teiytyxnirub">
        <Suspense fallback={<SectionLoader data-id="element-76" data-mp="1kffgwya80ihe" />} data-id="element-75" data-mp="1oj3p9j6ofi3p">
          <BusinessMapPage data-id="element-77" data-mp="1kg7docfk0m99" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-78" data-mp="2h4dawea5ccdi">
        <Suspense fallback={<SectionLoader data-id="element-80" data-mp="2c7mv7s297ism" />} data-id="element-79" data-mp="2c63e5z8rx3ps">
          <MembersDirectoryPage data-id="element-81" data-mp="2c7dg4ziqyw1p" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-82" data-mp="27dimb39fh0ow">
        <Suspense fallback={<SectionLoader data-id="element-84" data-mp="23c76fpgcvttw" />} data-id="element-83" data-mp="23ds1dg6l1esa">
          <VoicesOfImpactNYPage data-id="element-85" data-mp="2zvxpudhcwm8g" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-86" data-mp="2v57odbc1x3j1">
        <Suspense fallback={<SectionLoader data-id="element-88" data-mp="t7dx1d6lznea" />} data-id="element-87" data-mp="t7xbgjx1vtnk">
          <EncuentroCatrachoMiamiPage data-id="element-89" data-mp="t98gen28f7g9" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-90" data-mp="y8uusztsqtqn">
        <Suspense fallback={<SectionLoader data-id="element-92" data-mp="jhuds6ezhriw" />} data-id="element-91" data-mp="jhcet20xsz7a">
          <HonduranIndependenceDayPage data-id="element-93" data-mp="oh75p5kpposc" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94" data-mp="ageasjqi0rop">
        <Suspense fallback={<SectionLoader data-id="element-96" data-mp="fgslk9jhifqf" />} data-id="element-95" data-mp="fg940txqxtcx">
          <NationalNetworkPage data-id="element-97" data-mp="fi3pqagudpi2" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-98" data-mp="lcpy039abrx">
        <Suspense fallback={<SectionLoader data-id="element-100" data-mp="5il65v4ug5qj" />} data-id="element-99" data-mp="5j4fwokcbnth">
          <SaveTheDatePage data-id="element-101" data-mp="1ux9i5v9ivlo1" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-102" data-mp="1zwvwk81377yf">
        <Suspense fallback={<SectionLoader data-id="element-104" data-mp="3keelat4q16bf" />} data-id="element-103" data-mp="1zu9mo1qq4gd1">
          <MemberProfilePage slug={memberSlug} data-id="element-105" data-mp="3kdmurwpzdys0" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-106" data-mp="3fh4w9el8uwo4">
        <Suspense fallback={<SectionLoader data-id="element-108" data-mp="3u59fqks4fc7y" />} data-id="element-107" data-mp="3u4pzr9m16xwk">
          <NewsStoryPage postId={newsPostId} data-id="element-109" data-mp="3u5iypoer0j45" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-110" data-mp="3pbogkpeskc76" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-111" data-mp="6z8j3wv98ttd">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-112" data-mp="71l64ofvduda" />
      </LanguageProvider>
    </>;
}